import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
    createStyles,
    Paper,
    SnackbarContent,
    TextField,
    Theme,
    Typography,
    withStyles,
    WithStyles
} from "@material-ui/core";
import { Error as ErrorIcon } from "@material-ui/icons";
import { enqueueSnackbar } from "notistack";
import ReactGA from "react-ga4";
import { Controller, useForm } from "react-hook-form";
import { useHistory, useRouteMatch } from "react-router";
import * as z from "zod";
import {
    useResetPasswordMutation,
    useResetPasswordTokenCheckQuery
} from "../../api/accountApiSlice";
import Constants from "../../models/utility/Constants";
import TokenProvider from "../../providers/TokenProvider";
import FullscreenLoader from "../layout/FullscreenLoader";
import LoaderButton from "../shared/LoaderButton";
import SectionBanner from "../shared/SectionBanner";

const styles = (theme: Theme) =>
    createStyles({
        button: { marginTop: theme.spacing(3) },
        errorMessage: { textAlign: "left", marginTop: theme.spacing(2) },
        form: {
            maxWidth: 650,
            padding: theme.spacing(8),
            width: "100%"
        },
        formFieldTop: { marginTop: 0 },
        paper: {
            alignItems: "center",
            display: "flex",
            flexDirection: "column"
        },
        tokenErrorContainer: {
            backgroundColor: theme.palette.error.main,
            maxWidth: "100%",
            verticalAlign: "top",
            width: "100%"
        },
        tokenErrorIcon: {
            color: theme.palette.error.contrastText,
            marginRight: theme.spacing(),
            opacity: 0.9
        },
        tokenErrorMessage: {
            alignItems: "center",
            color: theme.palette.error.contrastText,
            display: "flex"
        }
    });

const schema = z
    .object({
        email: z.string().email("Please enter a valid email address"),
        password: z
            .string()
            .min(8, "Password must be at least 8 characters")
            .regex(/\d/, "Password must contain at least one digit"),
        confirmPassword: z.string()
    })
    .refine((data) => data.password === data.confirmPassword, {
        message: "Passwords must match",
        path: ["confirmPassword"]
    });

interface IPasswordResetFormValues extends z.infer<typeof schema> {}

interface IPasswordReset extends WithStyles<typeof styles> {}

function PasswordReset({ classes }: IPasswordReset) {
    const history = useHistory();
    const match = useRouteMatch<{ token: string }>(
        Constants.Routes.PasswordReset()
    );

    const {
        control,
        setError,
        formState: { errors },
        handleSubmit
    } = useForm<IPasswordResetFormValues>({
        defaultValues: {
            email: "",
            password: "",
            confirmPassword: ""
        },
        resolver: zodResolver(schema)
    });

    useEffect(() => {
        Constants.SetDocumentTitle("Password Reset");
    }, []);

    const resetPasswordToken = `${match?.params.token}`;

    const resetPasswordTokenCheckQuery =
        useResetPasswordTokenCheckQuery(resetPasswordToken);
    const [resetPasswordMutation, resetPasswordMutationData] =
        useResetPasswordMutation();

    const token = TokenProvider.GetStoredToken();
    if (token) {
        history.push(Constants.Routes.Profile());
    }

    if (resetPasswordTokenCheckQuery.isLoading) {
        return <FullscreenLoader />;
    }

    const onSubmit = async (data: IPasswordResetFormValues) => {
        try {
            ReactGA.event(Constants.GAEvents.Form.Submit);

            await resetPasswordMutation({
                Email: data.email,
                Token: resetPasswordToken,
                Password: data.password,
                ConfirmPassword: data.confirmPassword
            }).unwrap();

            enqueueSnackbar("Password reset successful completed", {
                anchorOrigin: { horizontal: "left", vertical: "top" },
                variant: "success"
            });

            history.push(Constants.Routes.Login());
        } catch (e) {
            ReactGA.event(Constants.GAEvents.Form.Invalid);

            setError("root", {
                message:
                    "Unknown error occurred while trying to reset your password"
            });

            enqueueSnackbar("Password reset failed", {
                anchorOrigin: { horizontal: "left", vertical: "top" },
                variant: "error"
            });

            throw e;
        }
    };

    const renderTokenError = () => {
        return (
            <SnackbarContent
                data-testid="resetPasswordTokenError"
                className={classes.tokenErrorContainer}
                message={
                    <span
                        id="client-snackbar"
                        className={classes.tokenErrorMessage}
                    >
                        <ErrorIcon className={classes.tokenErrorIcon} />
                        <p>This password reset link is invalid.</p>
                    </span>
                }
            />
        );
    };

    const renderForm = () => {
        return (
            <form
                onSubmit={handleSubmit(onSubmit)}
                data-testid="resetPasswordForm"
            >
                <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            autoFocus
                            className={classes.formFieldTop}
                            error={!!errors.email?.message}
                            fullWidth
                            label="Email"
                            margin="normal"
                            helperText={errors.email?.message}
                        />
                    )}
                />
                <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            autoComplete="new-password"
                            error={!!errors.password?.message}
                            fullWidth
                            helperText={
                                errors.password?.message ??
                                "Minimum length of 8 characters. One digit required."
                            }
                            label="Password"
                            margin="normal"
                            type="password"
                        />
                    )}
                />
                <Controller
                    name="confirmPassword"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            autoComplete="new-password"
                            error={!!errors.confirmPassword?.message}
                            fullWidth
                            helperText={errors.confirmPassword?.message}
                            label="Confirm Password"
                            margin="normal"
                            type="password"
                        />
                    )}
                />

                {errors.root && (
                    <Typography color="error" className={classes.errorMessage}>
                        {errors.root.message}
                    </Typography>
                )}

                <div className={classes.button}>
                    <LoaderButton
                        loading={resetPasswordMutationData.isLoading}
                        type="submit"
                        size="large"
                        variant="contained"
                        color="primary"
                    >
                        Reset Password
                    </LoaderButton>
                </div>
            </form>
        );
    };

    return (
        <Paper className={classes.paper} elevation={2}>
            <SectionBanner leftSideText="Password Reset" />
            <div className={classes.form}>
                {resetPasswordTokenCheckQuery.data
                    ? renderForm()
                    : renderTokenError()}
            </div>
        </Paper>
    );
}

export default withStyles(styles)(PasswordReset);
